



































































































































































































































































































































































































































































































































































































































































































































































































































import Vue from "vue";
import FormSection from "@/components/FormSection.vue";
import IconButton from "@/components/IconButton.vue";
import NavigationButton from "@/components/NavigationButton.vue";
import DateInput from "@/components/DateInput.vue";
import MoneyInput from "@/components/MoneyInput.vue";
import InvoiceItems from "../detailViews/InvoiceItems.vue";
import PartnerSearch from "@/modules/partner/views/PartnerSearch.vue";
import DisabledFieldTooltip from "../DisabledFieldTooltip.vue";
import store, { InterfaceStore } from "../../services/InterfaceStore";
import { optionDisplay } from "../../models/IfaceSyncDictionaryOption";
import configuration from "@/models/Configuration";
import { roundMoney } from "@/services/prices";
import eventBus from "@/services/eventBus";
import { Iface } from "../../models/Iface";
import { t } from "@/plugins/i18n";

interface Data {
  store: InterfaceStore;
  Iface: typeof Iface;
}

interface SelectItem {
  text: string;
  value: string;
}

interface Methods {
  saveremark(): Promise<void>;
  headDigits(currency: string | null): number;
  /** A partner választóból kiválasztott partner külső kódjának beírása a köztes tábla mezőjébe. */
  selectPartner(partner: any, isSecondary: boolean): Promise<void>;
}

interface Computed {
  locked: boolean;
  /** A beemelt bejövő számla (GENI) route-ja, ha van belső azonosító. */
  geniUrl: string;
  /** A tételekből összegzett bruttó eltér a számlafej (interfészről jött) bruttójától. */
  itemsCurrTotalMismatch: boolean;
  /** Fizetési mód választó elemei — a K2D megnevezés látszik, az érték a külső kód. */
  payModeItems: SelectItem[];
  /** Pénznem választó elemei — a köztes rétegből jött érték akkor is szerepel, ha nincs a K2D törzsben. */
  currencyItems: string[];
}

export default Vue.extend<Data, Methods, Computed, {}>({
  name: "SyncInvoiceEdit",

  components: {
    FormSection,
    IconButton,
    NavigationButton,
    DateInput,
    MoneyInput,
    InvoiceItems,
    PartnerSearch,
    DisabledFieldTooltip,
  },

  async beforeMount() {
    const id: number = Number(this.$route.params["id"]) || 0;
    await store.loadEditingInvoice(id);
    await store.loadEditingInvoicePayModeOptions();
    await store.loadEditingInvoiceTaxOptions();
    await store.loadCurrencies();
  },

  data() {
    return { store, Iface };
  },

  computed: {
    /**
     * Csak a szinkron sor legutolsó köztes tábla sora szerkeszthető, és csak addig, amíg a számla
     * nem került feldolgozásra és beemelésre. A tételrács is ezt kapja `readonly` propként.
     */
    locked(): boolean {
      return !this.store.editingInvoiceIsLatest || this.store.editorViewOnly || this.store.editingInvoiceIsImported;
    },

    /** A beemelt bejövő számla route-ja — kimenő számlánál és beemelés előtt nincs hova navigálni. */
    geniUrl(): string {
      const invoice = this.store.editingInvoice;
      return invoice.direction != "OUT" && invoice.geniId > 1
        ? `/general-incoming-invoice/flow/edit/${invoice.geniId}/2`
        : "";
    },

    /** Az összevetés a számla pénznemének pontosságán történik, hogy a kerekítés ne adjon hamis eltérést. */
    itemsCurrTotalMismatch(): boolean {
      const invoice = this.store.editingInvoice;
      if (!this.store.isInterface(Iface.BillCity) || invoice.itemsCurrTotal == null) return false;

      const currency = invoice.invCurrency ?? "HUF";
      const digits = this.headDigits(currency);
      return roundMoney(Number(invoice.itemsCurrTotal) || 0, currency, digits)
        != roundMoney(Number(invoice.currTotal) || 0, currency, digits);
    },

    payModeItems(): SelectItem[] {
      const items = this.store.payModeOptions.map((o) => ({ text: optionDisplay(o), value: o.extCode }));
      const payMode = this.store.editingInvoice.payMode;
      if (payMode && !items.some((i) => i.value === payMode)) {
        items.unshift({ text: payMode, value: payMode });
      }
      return items;
    },

    currencyItems(): string[] {
      const currency = this.store.editingInvoice.invCurrency;
      if (currency && !this.store.currencies.includes(currency)) {
        return [currency, ...this.store.currencies];
      }
      return this.store.currencies;
    },
  },

  methods: {
    /**
     * A partner választó a K2D partnereket kínálja, a köztes táblába viszont a partner interfészes
     * külső kódja kerül — ezt a partner köztes táblából oldjuk fel. Ha a partnerhez nincs külső kód
     * ezen az interfészen, nem írjuk át a mezőt, mert nem lenne mit visszaadni az interfésznek.
     */
    async selectPartner(partner: any, isSecondary: boolean) {
      if (!partner || partner.id <= 1) return;

      try {
        const extCode = await this.store.getEditingInvoicePartnerExtCode(partner.id);
        if (!extCode) {
          eventBus.$emit("error", t("Interface_APartnerhezNincsKulsoKodEzen", [partner.id, partner.name]));
          return;
        }

        if (isSecondary) {
          this.store.editingInvoice.partner2Code = extCode;
          this.store.editingInvoice.partner2Id = partner.id;
          this.store.editingInvoice.partner2Name = partner.name;
        } else {
          this.store.editingInvoice.partnerCode = extCode;
          this.store.editingInvoice.partnerId = partner.id;
          this.store.editingInvoice.partnerName = partner.name;
        }
      } catch (e) {
        console.error(e);
      }
    },

    /**
     * Szinkron megjegyzés mentése — csak a sync_remark + upddate/updtime/upduser mezők
     * módosulnak az adott köztes tábla soron, a usermodified flag NEM változik.
     */
    async saveremark() {
      try {
        await this.store.saveEditingInvoiceRemark();
      } catch (e) {
        console.error(e);
      }
    },

    /** Fej szintű összeg tizedesjegyek az adott pénznemhez (GENI fej digitek, HUF esetén 0). */
    headDigits(currency: string | null): number {
      return configuration.getCurrDigits(currency ?? "HUF")?.geniHeadDigits ?? 0;
    },
  },
});
